// Auth State Controller - Singleton pattern (like BoardStateController)

import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { AuthUser } from './auth-api.js';
import * as authApi from './auth-api.js';

export type AuthLoadingState = 'idle' | 'loading' | 'loaded' | 'error';

let sharedUser: AuthUser | null = null;
let sharedIsAuthenticated = false;
let sharedLoadingState: AuthLoadingState = 'idle';
let sharedError: string | null = null;
let sharedSessionExpired = false;

const hosts = new Set<ReactiveControllerHost>();

function notifyHosts(): void {
  for (const host of hosts) {
    host.requestUpdate();
  }
}

async function checkAuth(): Promise<void> {
  if (sharedLoadingState === 'loading') return;
  sharedLoadingState = 'loading';
  sharedError = null;
  notifyHosts();

  try {
    sharedUser = await authApi.getCurrentUser();
    sharedIsAuthenticated = true;
    sharedLoadingState = 'loaded';
    sharedSessionExpired = false;
  } catch (e) {
    if (e instanceof authApi.AuthError && e.status === 401) {
      sharedUser = null;
      sharedIsAuthenticated = false;
      sharedLoadingState = 'loaded';
    } else {
      sharedLoadingState = 'error';
      sharedError = e instanceof Error ? e.message : 'Auth-Check fehlgeschlagen';
    }
  }

  notifyHosts();
}

async function performLogout(): Promise<void> {
  try {
    await authApi.logout();
  } catch {
    // Clear local state even if request fails
  }
  sharedUser = null;
  sharedIsAuthenticated = false;
  sharedLoadingState = 'loaded';
  sharedSessionExpired = false;
  notifyHosts();
}

function handleSessionExpired(): void {
  sharedUser = null;
  sharedIsAuthenticated = false;
  sharedLoadingState = 'loaded';
  sharedSessionExpired = true;
  notifyHosts();
}

export class AuthStateController implements ReactiveController {
  private host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    hosts.add(this.host);
    if (sharedLoadingState === 'idle') {
      void checkAuth();
    }
  }

  hostDisconnected(): void {
    hosts.delete(this.host);
  }

  get isAuthenticated(): boolean {
    return sharedIsAuthenticated;
  }

  get currentUser(): AuthUser | null {
    return sharedUser;
  }

  get isLoading(): boolean {
    return sharedLoadingState === 'idle' || sharedLoadingState === 'loading';
  }

  get loadingState(): AuthLoadingState {
    return sharedLoadingState;
  }

  get error(): string | null {
    return sharedError;
  }

  get sessionExpired(): boolean {
    return sharedSessionExpired;
  }

  async checkAuth(): Promise<void> {
    await checkAuth();
  }

  async logout(): Promise<void> {
    await performLogout();
  }

  markSessionExpired(): void {
    handleSessionExpired();
  }
}
